import axios from 'axios';

export interface RetryOptions {
  /** Total number of retry attempts after the initial try. Default: 3. */
  retries?: number;
  /** Base delay in ms for exponential backoff. Default: 750. */
  baseMs?: number;
  /** Upper bound on the delay between attempts. Default: 6000. */
  maxMs?: number;
  /**
   * Custom predicate to decide whether an error is worth retrying. By
   * default we retry network errors, axios timeouts and 408/429/5xx.
   */
  shouldRetry?: (error: unknown) => boolean;
  /** Optional hook fired before each retry sleep (useful for logging). */
  onRetry?: (error: unknown, attempt: number, delayMs: number) => void;
}

/**
 * Retry-classifier for axios responses we expect to be safely retriable.
 *
 * Includes the failure modes that show up when the backend's heavy-query
 * semaphore is saturated and the API gateway gives up on the connection
 * before the eventual 200 reaches the client (504s, ECONNABORTED, dropped
 * sockets, etc).
 */
export function isTransientError(error: unknown): boolean {
  if (!error) return false;
  if (axios.isCancel(error)) return false;

  if (axios.isAxiosError(error)) {
    if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') return true;
    const status = error.response?.status;
    // No response at all (network failure / connection reset) — retriable.
    if (!status) return true;
    return status === 408 || status === 429 || status === 502 || status === 503 || status === 504;
  }

  return false;
}

/**
 * Run `fn` and retry on transient failures with exponential backoff + jitter.
 *
 * The backoff schedule is `min(maxMs, baseMs * 2^(attempt - 1))` plus up to
 * 30% jitter. Non-transient errors (per `shouldRetry`) are rethrown
 * immediately.
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    retries = 3,
    baseMs = 750,
    maxMs = 6000,
    shouldRetry = isTransientError,
    onRetry,
  } = options;

  let attempt = 0;
  // The loop guard is `attempt > retries`; we always run the initial try.
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      return await fn();
    } catch (error) {
      attempt++;
      if (attempt > retries || !shouldRetry(error)) throw error;

      const expDelay = Math.min(maxMs, baseMs * Math.pow(2, attempt - 1));
      const jitter = Math.random() * expDelay * 0.3;
      const delay = Math.round(expDelay + jitter);

      onRetry?.(error, attempt, delay);

      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
}
